import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import { LoyaltyPlan, CreateLoyaltyPlanDto, UpdateLoyaltyPlanDto, LoyaltyPlanType, RewardType, PlanStatus, PointOrigin } from '../types';
import './Pages.css';

const LoyaltyPlans: React.FC = () => {
  const [plans, setPlans] = useState<LoyaltyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activatingPlanId, setActivatingPlanId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateLoyaltyPlanDto>({
    name: '',
    description: '',
    type: LoyaltyPlanType.ByPoints,
    minimumValue: '',
    targetPoints: '',
    rewardType: RewardType.MoneyDiscount,
    rewardDescription: '',
    rewardValue: '',
    activeOrigins: [],
    pointsPerOrigin: undefined
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get<LoyaltyPlan[]>('/admin/loyalty-plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    try {
      if (editingPlanId) {
        // Update existing plan
        const payload: UpdateLoyaltyPlanDto = {
          name: formData.name,
          description: formData.description || undefined,
          minimumValue: typeof formData.minimumValue === 'string' 
            ? parseFloat(formData.minimumValue) 
            : formData.minimumValue,
          targetPoints: typeof formData.targetPoints === 'string'
            ? parseInt(formData.targetPoints, 10)
            : formData.targetPoints,
          rewardType: formData.rewardType,
          rewardDescription: formData.rewardDescription,
          rewardValue: formData.rewardValue && formData.rewardValue !== ''
            ? (typeof formData.rewardValue === 'string' 
                ? parseFloat(formData.rewardValue) 
                : formData.rewardValue)
            : undefined,
          activeOrigins: formData.activeOrigins && formData.activeOrigins.length > 0 ? formData.activeOrigins : undefined,
          pointsPerOrigin: formData.pointsPerOrigin && Object.keys(formData.pointsPerOrigin).length > 0 ? formData.pointsPerOrigin : undefined
        };
        
        await api.put(`/admin/loyalty-plans/${editingPlanId}`, payload);
      } else {
        // Create new plan
        const payload = {
          name: formData.name,
          description: formData.description || null,
          type: formData.type,
          minimumValue: typeof formData.minimumValue === 'string' 
            ? parseFloat(formData.minimumValue) 
            : formData.minimumValue,
          targetPoints: typeof formData.targetPoints === 'string'
            ? parseInt(formData.targetPoints, 10)
            : formData.targetPoints,
          rewardType: formData.rewardType,
          rewardDescription: formData.rewardDescription,
          rewardValue: formData.rewardValue && formData.rewardValue !== ''
            ? (typeof formData.rewardValue === 'string' 
                ? parseFloat(formData.rewardValue) 
                : formData.rewardValue)
            : null,
          activeOrigins: formData.activeOrigins && formData.activeOrigins.length > 0 ? formData.activeOrigins : undefined,
          pointsPerOrigin: formData.pointsPerOrigin && Object.keys(formData.pointsPerOrigin).length > 0 ? formData.pointsPerOrigin : undefined
        };
        
        await api.post('/admin/loyalty-plans', payload);
      }
      
      setShowModal(false);
      setEditingPlanId(null);
      loadPlans();
      resetForm();
    } catch (error: any) {
      console.error('Error saving plan:', error);
      const errorMessage = error.response?.data?.message || (editingPlanId ? 'Erro ao editar plano' : 'Erro ao criar plano');
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const activatePlan = async (planId: string) => {
    if (activatingPlanId) return;
    
    setActivatingPlanId(planId);
    try {
      await api.post(`/admin/loyalty-plans/${planId}/activate`);
      loadPlans();
    } catch (error) {
      console.error('Error activating plan:', error);
      alert('Erro ao ativar plano');
    } finally {
      setActivatingPlanId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: LoyaltyPlanType.ByPoints,
      minimumValue: '',
      targetPoints: '',
      rewardType: RewardType.MoneyDiscount,
      rewardDescription: '',
      rewardValue: '',
      activeOrigins: [],
      pointsPerOrigin: undefined
    });
    setEditingPlanId(null);
  };

  const handleEditPlan = (plan: LoyaltyPlan) => {
    setFormData({
      name: plan.name,
      description: plan.description || '',
      type: plan.type,
      minimumValue: plan.minimumValue.toString(),
      targetPoints: plan.targetPoints.toString(),
      rewardType: plan.rewardType,
      rewardDescription: plan.rewardDescription,
      rewardValue: plan.rewardValue?.toString() || '',
      activeOrigins: plan.activeOrigins || [],
      pointsPerOrigin: plan.pointsPerOrigin || undefined
    });
    setEditingPlanId(plan.planId);
    setShowModal(true);
  };

  const handleNewPlan = () => {
    resetForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlanId(null);
    resetForm();
  };

  const getOriginName = (origin: PointOrigin): string => {
    switch (origin) {
      case PointOrigin.LojaFisica:
        return 'Loja Física';
      case PointOrigin.Telefone:
        return 'Pedido por Telefone';
      case PointOrigin.Delivery:
        return 'Delivery';
      case PointOrigin.Ifood:
        return 'iFood';
      default:
        return 'Desconhecido';
    }
  };

  const handleOriginToggle = (origin: PointOrigin) => {
    setFormData(prev => {
      const activeOrigins = prev.activeOrigins || [];
      const newActiveOrigins = activeOrigins.includes(origin)
        ? activeOrigins.filter(o => o !== origin)
        : [...activeOrigins, origin];
      
      const pointsPerOrigin = { ...(prev.pointsPerOrigin || {}) };
      if (!newActiveOrigins.includes(origin)) {
        delete pointsPerOrigin[origin];
      } else if (!pointsPerOrigin[origin]) {
        pointsPerOrigin[origin] = 1;
      }
      
      return {
        ...prev,
        activeOrigins: newActiveOrigins,
        pointsPerOrigin: Object.keys(pointsPerOrigin).length > 0 ? pointsPerOrigin : undefined
      };
    });
  };

  const handlePointsPerOriginChange = (origin: PointOrigin, value: string) => {
    setFormData(prev => ({
      ...prev,
      pointsPerOrigin: {
        ...(prev.pointsPerOrigin || {}),
        [origin]: parseInt(value) || 1
      }
    }));
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'type' || name === 'rewardType' ? parseInt(value) : value
    }));
  };

  const getStatusText = (status: PlanStatus): string => {
    switch (status) {
      case PlanStatus.Active:
        return 'Ativo';
      case PlanStatus.Inactive:
        return 'Inativo';
      case PlanStatus.Expired:
        return 'Expirado';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusClassName = (status: PlanStatus): string => {
    switch (status) {
      case PlanStatus.Active:
        return 'active';
      case PlanStatus.Inactive:
        return 'inactive';
      case PlanStatus.Expired:
        return 'expired';
      default:
        return 'inactive';
    }
  };

  if (loading) return <Loading message="Carregando planos..." />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Planos de Fidelidade</h1>
          <p>Gerencie seus programas de pontos</p>
        </div>
        <button className="btn-primary" onClick={handleNewPlan}>
          + Novo Plano
        </button>
      </div>

      <div className="plans-grid">
        {plans.map((plan) => (
          <div key={plan.planId} className={`plan-card ${getStatusClassName(plan.status)}`}>
            <div className="plan-header">
              <h3>{plan.name}</h3>
              <span className={`status-badge ${getStatusClassName(plan.status)}`}>
                {getStatusText(plan.status)}
              </span>
            </div>
            <p className="plan-description">{plan.description}</p>
            <div className="plan-info">
              <div className="info-item">
                <span className="info-label">Tipo:</span>
                <span>{plan.type === LoyaltyPlanType.ByPoints ? 'Por Pontuação' : 'Por Utilização'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Meta:</span>
                <span>{plan.targetPoints} pontos</span>
              </div>
              <div className="info-item">
                <span className="info-label">Valor Mínimo:</span>
                <span>R$ {plan.minimumValue}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Recompensa:</span>
                <span>{plan.rewardDescription}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', marginTop: '20px' }}>
              <button
                className="btn-secondary"
                onClick={() => handleEditPlan(plan)}
                style={{ width: '100%' }}
              >
                Editar Plano
              </button>
              {plan.status !== PlanStatus.Active && (
                <button
                  className="btn-activate"
                  onClick={() => activatePlan(plan.planId)}
                  disabled={activatingPlanId === plan.planId}
                >
                  {activatingPlanId === plan.planId ? 'Ativando...' : 'Ativar Plano'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPlanId ? 'Editar Plano' : 'Criar Novo Plano'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome do Plano</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              {!editingPlanId && (
                <div className="form-group">
                  <label>Tipo de Plano</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value={LoyaltyPlanType.ByPoints}>Por Pontuação (R$1 = 1 ponto)</option>
                    <option value={LoyaltyPlanType.ByUsage}>Por Utilização (1 visita = 1 ponto)</option>
                  </select>
                </div>
              )}
              {editingPlanId && (
                <div className="form-group">
                  <label>Tipo de Plano</label>
                  <input
                    type="text"
                    value={formData.type === LoyaltyPlanType.ByPoints ? 'Por Pontuação (R$1 = 1 ponto)' : 'Por Utilização (1 visita = 1 ponto)'}
                    disabled
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <small className="form-hint" style={{ display: 'block', marginTop: '5px' }}>
                    O tipo de plano não pode ser alterado após a criação
                  </small>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Valor Mínimo</label>
                  <input
                    type="number"
                    name="minimumValue"
                    required
                    step="0.01"
                    value={formData.minimumValue}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Meta de Pontos</label>
                  <input
                    type="number"
                    name="targetPoints"
                    required
                    value={formData.targetPoints}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Tipo de Recompensa</label>
                <select
                  name="rewardType"
                  value={formData.rewardType}
                  onChange={handleInputChange}
                >
                  <option value={RewardType.MoneyDiscount}>Desconto em Dinheiro</option>
                  <option value={RewardType.FreeDish}>Prato Grátis</option>
                  <option value={RewardType.FreeDessert}>Sobremesa Grátis</option>
                  <option value={RewardType.Custom}>Personalizado</option>
                </select>
              </div>
              <div className="form-group">
                <label>Descrição da Recompensa</label>
                <input
                  type="text"
                  name="rewardDescription"
                  required
                  value={formData.rewardDescription}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label>Origens de Pontuação Ativas</label>
                <div className="form-hint">
                  Selecione as origens que deseja permitir para este plano de fidelidade
                </div>
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Object.values(PointOrigin).filter(v => typeof v === 'number').map((origin) => (
                    <div key={origin} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="checkbox"
                        checked={(formData.activeOrigins || []).includes(origin as PointOrigin)}
                        onChange={() => handleOriginToggle(origin as PointOrigin)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label style={{ flex: 1, cursor: 'pointer', fontWeight: 500 }}>
                        {getOriginName(origin as PointOrigin)}
                      </label>
                      {(formData.activeOrigins || []).includes(origin as PointOrigin) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{ fontSize: '13px', color: 'var(--text-gray)' }}>Pontos:</label>
                          <input
                            type="number"
                            min="1"
                            value={formData.pointsPerOrigin?.[origin as PointOrigin] || 1}
                            onChange={(e) => handlePointsPerOriginChange(origin as PointOrigin, e.target.value)}
                            style={{
                              width: '80px',
                              padding: '6px 10px',
                              border: '2px solid var(--border)',
                              borderRadius: '8px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting 
                    ? (editingPlanId ? 'Salvando...' : 'Criando...') 
                    : (editingPlanId ? 'Salvar Alterações' : 'Criar Plano')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyPlans;


